import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Avatar, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, FormControl, InputLabel, Autocomplete, Paper, Divider, Badge,
  Menu, ListItemIcon, ListItemText, LinearProgress, ToggleButtonGroup, ToggleButton,
  Drawer, Popover, List, ListItem, ListItemButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlagIcon from '@mui/icons-material/Flag';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { rndService } from '../services/api';
import api from '../services/api';

const PRIORITIES = [
  { value: 'low',    label: 'Низький',   color: '#4caf50', icon: '▼' },
  { value: 'medium', label: 'Середній',  color: '#2196f3', icon: '●' },
  { value: 'high',   label: 'Високий',   color: '#ff9800', icon: '▲' },
  { value: 'urgent', label: 'Терміново', color: '#f44336', icon: '⚡' },
];

const priorityInfo = (v) => PRIORITIES.find((p) => p.value === v) || PRIORITIES[1];

function hexToRgb(hex, alpha = 0.15) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function DeadlineChip({ date }) {
  if (!date) return null;
  const d = new Date(date);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const overdue = d < now;
  const today = d.toDateString() === now.toDateString();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 11,
      color: overdue ? '#f44336' : today ? '#ff9800' : 'text.secondary' }}>
      <CalendarTodayIcon sx={{ fontSize: 11 }} />
      {d.toLocaleDateString('uk-UA', { day: '2-digit', month: 'short' })}
    </Box>
  );
}

function AssigneeAvatars({ assignees, max = 3 }) {
  const visible = assignees.slice(0, max);
  const rest = assignees.length - max;
  return (
    <Box sx={{ display: 'flex' }}>
      {visible.map((a) => (
        <Tooltip key={a.id} title={a.name}>
          <Avatar sx={{ width: 20, height: 20, fontSize: 9, ml: -0.4, border: '1px solid', borderColor: 'background.paper', bgcolor: 'primary.main' }}>
            {initials(a.name)}
          </Avatar>
        </Tooltip>
      ))}
      {rest > 0 && (
        <Avatar sx={{ width: 20, height: 20, fontSize: 9, ml: -0.4, border: '1px solid', borderColor: 'background.paper', bgcolor: 'grey.500' }}>
          +{rest}
        </Avatar>
      )}
    </Box>
  );
}

// ─── Inline Status Picker ─────────────────────────────────────────────────────
function StatusPicker({ current, statuses, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const si = statuses.find((s) => s.id === current?.id) || current;
  return (
    <>
      <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: 0.8, cursor: 'pointer',
          bgcolor: si ? hexToRgb(si.color, 0.18) : 'action.hover',
          border: '1px solid', borderColor: si ? si.color + '55' : 'divider',
          '&:hover': { filter: 'brightness(0.92)' }, userSelect: 'none', fontSize: 11, fontWeight: 700,
          color: si?.color || 'text.secondary', minWidth: 80, justifyContent: 'center',
        }}>
        {si?.name || 'Без статусу'}
        <KeyboardArrowDownIcon sx={{ fontSize: 12 }} />
      </Box>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ py: 0.5, minWidth: 160 }}>
          {statuses.map((s) => (
            <ListItemButton key={s.id} dense onClick={() => { onChange(s); setAnchor(null); }}
              sx={{ px: 1.5, py: 0.6, gap: 1, '&:hover': { bgcolor: hexToRgb(s.color, 0.12) } }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: s.color }}>{s.name}</Typography>
            </ListItemButton>
          ))}
        </Box>
      </Popover>
    </>
  );
}

// ─── Task Card (Kanban) ───────────────────────────────────────────────────────
function TaskCard({ task, onOpen, onSubtaskOpen, statuses, onStatusChange }) {
  const pi = priorityInfo(task.priority);
  const progress = task.subtasks_count > 0 ? Math.round((task.subtasks_done / task.subtasks_count) * 100) : null;
  const sd = task.status_data;

  return (
    <Paper
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('taskId', String(task.id)); e.stopPropagation(); }}
      onClick={() => onOpen(task.id)}
      elevation={0}
      sx={{
        p: 1.5, mb: 1, cursor: 'pointer',
        border: '1px solid', borderColor: 'divider',
        borderRadius: 1.5,
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderColor: sd?.color || 'primary.main' },
        transition: 'box-shadow .15s, border-color .15s',
        position: 'relative',
        '&::before': sd ? {
          content: '""', position: 'absolute', left: 0, top: 4, bottom: 4,
          width: 3, bgcolor: sd.color, borderRadius: '0 2px 2px 0',
        } : {},
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35, fontSize: 13, mb: 1, pr: 1 }}>
        {task.title}
      </Typography>
      {progress !== null && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
            {progress === 100
              ? <CheckBoxIcon sx={{ fontSize: 12, color: 'success.main' }} />
              : <CheckBoxOutlineBlankIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
            <Typography variant="caption" sx={{ fontSize: 10, color: progress === 100 ? 'success.main' : 'text.secondary', fontWeight: 600 }}>
              {task.subtasks_done}/{task.subtasks_count} підзадач
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10, color: progress === 100 ? 'success.main' : 'text.secondary', fontWeight: 700, ml: 'auto' }}>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress}
            sx={{ height: 3, borderRadius: 2, bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: progress === 100 ? 'success.main' : (sd?.color || 'primary.main') } }} />
          {(task.subtasks_preview || []).map((st) => (
            <Box key={st.id} onClick={(e) => { e.stopPropagation(); onSubtaskOpen && onSubtaskOpen(st.id); }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 0.5, py: 0.3,
                borderRadius: 0.8, cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}>
              {st.is_done
                ? <CheckBoxIcon sx={{ fontSize: 12, color: 'success.main', flexShrink: 0 }} />
                : <CheckBoxOutlineBlankIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />}
              <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.3, flex: 1,
                color: st.is_done ? 'text.secondary' : 'text.primary',
                textDecoration: st.is_done ? 'line-through' : 'none' }}>
                {st.title}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={pi.label}>
          <span style={{ fontSize: 11, color: pi.color, fontWeight: 700 }}>{pi.icon}</span>
        </Tooltip>
        <DeadlineChip date={task.deadline} />
        <Box sx={{ ml: 'auto' }}>
          <AssigneeAvatars assignees={task.assignees_data || []} />
        </Box>
      </Box>
    </Paper>
  );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────
function KanbanColumn({ status, tasks, onOpen, onSubtaskOpen, onDrop, onAddTask, statuses, onStatusChange }) {
  const [over, setOver] = useState(false);

  return (
    <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { setOver(false); onDrop(e, status.id); }}>
      {/* Column header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, px: 0.5, gap: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: 12, color: status.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {status.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{tasks.length}</Typography>
      </Box>
      {/* Cards area */}
      <Box sx={{
        flex: 1, minHeight: 60, p: 1, borderRadius: 1.5,
        bgcolor: over ? hexToRgb(status.color, 0.08) : 'action.hover',
        border: '2px dashed', borderColor: over ? status.color : 'transparent',
        transition: 'all .15s',
      }}>
        {tasks.map((t) => (
          <TaskCard key={t.id} task={t} onOpen={onOpen} onSubtaskOpen={onSubtaskOpen} statuses={statuses} onStatusChange={onStatusChange} />
        ))}
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
          onClick={() => onAddTask(status.id)}
          sx={{ width: '100%', justifyContent: 'flex-start', color: 'text.disabled', mt: 0.5, fontSize: 11,
            '&:hover': { color: status.color, bgcolor: hexToRgb(status.color, 0.08) } }}>
          Додати задачу
        </Button>
      </Box>
    </Box>
  );
}

// ─── List Row ─────────────────────────────────────────────────────────────────
function ListRow({ task, onOpen, onSubtaskOpen, statuses, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const pi = priorityInfo(task.priority);
  const sd = task.status_data;
  const hasSub = task.subtasks_count > 0;
  const allDone = hasSub && task.subtasks_done === task.subtasks_count;
  const subs = task.subtasks_preview || [];

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.8,
        '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer' }}>
        {/* Expand toggle */}
        <Box sx={{ width: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasSub ? (
            <IconButton size="small" sx={{ p: 0, width: 16, height: 16 }}
              onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}>
              <KeyboardArrowDownIcon sx={{ fontSize: 14, color: 'text.secondary',
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
            </IconButton>
          ) : (
            <span style={{ color: pi.color, fontSize: 10, fontWeight: 700 }}>{pi.icon}</span>
          )}
        </Box>
        <Box onClick={(e) => e.stopPropagation()}>
          <StatusPicker current={sd} statuses={statuses} onChange={(s) => onStatusChange(task.id, s)} />
        </Box>
        <Typography variant="body2" onClick={() => onOpen(task.id)} sx={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{task.title}</Typography>
        {hasSub && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => onOpen(task.id)}>
            {allDone
              ? <CheckBoxIcon sx={{ fontSize: 13, color: 'success.main' }} />
              : <CheckBoxOutlineBlankIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600,
              color: allDone ? 'success.main' : 'text.secondary' }}>
              {task.subtasks_done}/{task.subtasks_count}
            </Typography>
            <Box sx={{ width: 48 }}>
              <LinearProgress variant="determinate"
                value={Math.round(task.subtasks_done / task.subtasks_count * 100)}
                sx={{ height: 3, borderRadius: 2,
                  '& .MuiLinearProgress-bar': { bgcolor: allDone ? 'success.main' : (sd?.color || 'primary.main') } }} />
            </Box>
            <Typography variant="caption" sx={{ fontSize: 10, color: allDone ? 'success.main' : 'text.secondary' }}>
              {Math.round(task.subtasks_done / task.subtasks_count * 100)}%
            </Typography>
          </Box>
        )}
        <DeadlineChip date={task.deadline} />
        <AssigneeAvatars assignees={task.assignees_data || []} />
      </Box>
      {/* Subtask rows */}
      {expanded && subs.map((st) => (
        <Box key={st.id} onClick={() => onSubtaskOpen && onSubtaskOpen(st.id)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.6,
            pl: 5.5, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider',
            cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}>
          {st.is_done
            ? <CheckBoxIcon sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
            : <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />}
          <Typography variant="body2" sx={{ fontSize: 12, color: st.is_done ? 'text.secondary' : 'text.primary',
            textDecoration: st.is_done ? 'line-through' : 'none', flex: 1 }}>
            {st.title}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>↗</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Task Detail Drawer ───────────────────────────────────────────────────────
function TaskDrawer({ taskId, open, onClose, onSaved, staffList, currentUser, statuses, onSubtaskOpen }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [editDesc, setEditDesc] = useState(false);
  const [form, setForm] = useState({});
  const [newSubtask, setNewSubtask] = useState('');
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    const r = await rndService.getTask(taskId);
    setTask(r.data);
    setForm({
      title: r.data.title, description: r.data.description,
      status: r.data.status, priority: r.data.priority,
      deadline: r.data.deadline || '', assignees: r.data.assignees || [],
    });
    setLoading(false);
  }, [taskId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = async (data) => {
    await rndService.updateTask(taskId, data);
    load();
    onSaved();
  };

  const addSubtask = async () => {
    if (!newSubtask.trim()) return;
    await rndService.createSubtask({ task: taskId, title: newSubtask.trim() });
    setNewSubtask('');
    load(); onSaved();
  };

  const toggleSubtask = async (st) => {
    await rndService.updateSubtask(st.id, { is_done: !st.is_done });
    load(); onSaved();
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    await rndService.createComment({ task: taskId, text: newComment.trim(), author: currentUser?.id });
    setNewComment('');
    load();
  };

  if (!task && !loading) return null;
  const pi = priorityInfo(task?.priority);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 600 }, display: 'flex', flexDirection: 'column' } }}>
      {loading || !task ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Top bar */}
          <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Задача</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {/* Title */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
              {editTitle ? (
                <TextField fullWidth variant="standard" value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  onBlur={() => { patch({ title: form.title }); setEditTitle(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { patch({ title: form.title }); setEditTitle(false); } }}
                  autoFocus inputProps={{ style: { fontWeight: 700, fontSize: 20 } }} />
              ) : (
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: 20, cursor: 'text', '&:hover': { opacity: 0.8 } }}
                  onClick={() => setEditTitle(true)}>
                  {task.title}
                </Typography>
              )}
            </Box>

            {/* Meta fields */}
            <Box sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Статус</Typography>
                <StatusPicker current={task.status_data} statuses={statuses}
                  onChange={(s) => patch({ status: s.id })} />
              </Box>
              {/* Priority */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Пріоритет</Typography>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select value={form.priority} onChange={(e) => { setForm(f => ({ ...f, priority: e.target.value })); patch({ priority: e.target.value }); }}
                    variant="outlined" sx={{ fontSize: 12 }}>
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p.value} value={p.value} sx={{ fontSize: 12 }}>
                        <span style={{ color: p.color, marginRight: 6 }}>{p.icon}</span> {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {/* Deadline */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Дедлайн</Typography>
                <TextField type="date" size="small" value={form.deadline}
                  onChange={(e) => { setForm(f => ({ ...f, deadline: e.target.value })); patch({ deadline: e.target.value || null }); }}
                  InputLabelProps={{ shrink: true }} sx={{ width: 160, '& input': { fontSize: 12 } }} />
              </Box>
              {/* Completed at */}
              {task.completed_at && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Виконано</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckBoxIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                      {new Date(task.completed_at).toLocaleDateString('uk-UA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Box>
              )}
              {/* Assignees */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0, mt: 1 }}>Виконавці</Typography>
                <Autocomplete multiple size="small" options={staffList}
                  getOptionLabel={(o) => o.full_name || o.username}
                  value={staffList.filter((s) => form.assignees.includes(s.id))}
                  onChange={(_, val) => { const ids = val.map(v => v.id); setForm(f => ({ ...f, assignees: ids })); patch({ assignees: ids }); }}
                  renderTags={(val, getProps) => val.map((opt, i) => (
                    <Chip key={opt.id} label={opt.full_name || opt.username} size="small" {...getProps({ index: i })} />
                  ))}
                  renderInput={(params) => <TextField {...params} placeholder="Оберіть..." sx={{ '& input': { fontSize: 12 } }} />}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Description */}
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>Опис</Typography>
              {editDesc ? (
                <TextField fullWidth multiline minRows={3} size="small" value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  onBlur={() => { patch({ description: form.description }); setEditDesc(false); }}
                  autoFocus />
              ) : (
                <Typography variant="body2" onClick={() => setEditDesc(true)} sx={{
                  whiteSpace: 'pre-wrap', fontSize: 13, cursor: 'text', minHeight: 36, p: 1, borderRadius: 1,
                  color: task.description ? 'text.primary' : 'text.disabled',
                  border: '1px dashed transparent', '&:hover': { borderColor: 'divider' },
                }}>
                  {task.description || 'Натисніть щоб додати опис...'}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Subtasks */}
            <Box sx={{ px: 3, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  Підзадачі {task.subtasks?.length > 0 && `(${task.subtasks.filter(s => s.is_done).length}/${task.subtasks.length})`}
                </Typography>
              </Box>
              {task.subtasks?.length > 0 && (
                <LinearProgress variant="determinate"
                  value={Math.round(task.subtasks.filter(s => s.is_done).length / task.subtasks.length * 100)}
                  sx={{ mb: 1, height: 4, borderRadius: 2 }} />
              )}
              {task.subtasks?.map((st) => (
                <Box key={st.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.4, '&:hover .del-st': { opacity: 1 } }}>
                  <IconButton size="small" onClick={() => toggleSubtask(st)} sx={{ p: 0.3 }}>
                    {st.is_done
                      ? <CheckBoxIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      : <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                  </IconButton>
                  <Typography variant="body2" onClick={() => onSubtaskOpen && onSubtaskOpen(st.id)}
                    sx={{ flex: 1, fontSize: 13, textDecoration: st.is_done ? 'line-through' : 'none',
                      color: st.is_done ? 'text.secondary' : 'text.primary', cursor: 'pointer',
                      '&:hover': { color: 'primary.main', textDecoration: st.is_done ? 'line-through' : 'underline' } }}>
                    {st.title}
                  </Typography>
                  <IconButton size="small" className="del-st" sx={{ p: 0.3, opacity: 0, transition: 'opacity .15s' }}
                    onClick={() => { rndService.deleteSubtask(st.id).then(() => { load(); onSaved(); }); }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField size="small" placeholder="Нова підзадача..." value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addSubtask()}
                  sx={{ flex: 1, '& input': { fontSize: 12 } }} />
                <Button size="small" variant="outlined" onClick={addSubtask} sx={{ minWidth: 36, px: 1 }}>
                  <AddIcon fontSize="small" />
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* Comments */}
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Коментарі {task.comments?.length > 0 && `(${task.comments.length})`}
              </Typography>
              {task.comments?.map((c) => (
                <Box key={c.id} sx={{ mb: 2, '&:hover .del-c': { opacity: 1 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'secondary.main' }}>{initials(c.author_name)}</Avatar>
                    <Typography variant="caption" fontWeight={700}>{c.author_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: 10 }}>
                      {new Date(c.created_at).toLocaleDateString('uk-UA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {currentUser?.id === c.author && (
                      <IconButton size="small" className="del-c" sx={{ p: 0.3, opacity: 0, transition: 'opacity .15s' }}
                        onClick={() => rndService.deleteComment(c.id).then(load)}>
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ ml: 4, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
                  </Box>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.main', flexShrink: 0 }}>
                  {initials(currentUser?.full_name || currentUser?.username)}
                </Avatar>
                <TextField multiline maxRows={4} size="small" placeholder="Написати коментар..." value={newComment}
                  onChange={(e) => setNewComment(e.target.value)} sx={{ flex: 1, '& textarea': { fontSize: 13 } }} />
                <IconButton onClick={addComment} color="primary" disabled={!newComment.trim()} sx={{ flexShrink: 0 }}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
}

// ─── Status Settings Dialog ───────────────────────────────────────────────────
function StatusSettings({ open, onClose, boardId, statuses, onSaved }) {
  const [list, setList] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#9e9e9e');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setList(statuses); }, [statuses, open]);

  const addStatus = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    await rndService.createStatus({ board: boardId, name: newName.trim(), color: newColor, order: list.length });
    setNewName(''); setNewColor('#9e9e9e'); setSaving(false);
    onSaved();
  };

  const updateStatus = async (id, data) => {
    await rndService.updateStatus(id, data);
    onSaved();
  };

  const deleteStatus = async (id) => {
    await rndService.deleteStatus(id);
    onSaved();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon fontSize="small" /> Статуси дошки
      </DialogTitle>
      <DialogContent>
        {list.map((s) => (
          <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <input type="color" value={s.color}
              onChange={(e) => updateStatus(s.id, { color: e.target.value })}
              style={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
            <TextField size="small" value={s.name} sx={{ flex: 1 }}
              onChange={(e) => updateStatus(s.id, { name: e.target.value })} />
            <Tooltip title={s.is_done ? 'Фінальний статус' : 'Зробити фінальним'}>
              <IconButton size="small" onClick={() => updateStatus(s.id, { is_done: !s.is_done })}
                sx={{ color: s.is_done ? 'success.main' : 'text.disabled' }}>
                <CheckBoxIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" color="error" onClick={() => deleteStatus(s.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>Новий статус</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
            style={{ width: 28, height: 28, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
          <TextField size="small" placeholder="Назва статусу" value={newName}
            onChange={(e) => setNewName(e.target.value)} sx={{ flex: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && addStatus()} />
          <Button size="small" variant="contained" onClick={addStatus} disabled={!newName.trim() || saving}>
            <AddIcon fontSize="small" />
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Contractors Directory Dialog ────────────────────────────────────────────
function ContractorsDirectoryDialog({ open, onClose, contractorList, setContractorList }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', edrpou: '', address: '', contact_full_name: '', contact_phone: '', contact_email: '', notes: '' });
  const [formOpen, setFormOpen] = useState(false);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', edrpou: '', address: '', contact_full_name: '', contact_phone: '', contact_email: '', notes: '' });
    setFormOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, edrpou: c.edrpou || '', address: c.address || '', contact_full_name: c.contact_full_name || '', contact_phone: c.contact_phone || '', contact_email: c.contact_email || '', notes: c.notes || '' });
    setFormOpen(true);
  };

  const save = async () => {
    if (editing) {
      const r = await rndService.updateContractor(editing.id, form);
      setContractorList(list => list.map(c => c.id === editing.id ? r.data : c));
    } else {
      const r = await rndService.createContractor(form);
      setContractorList(list => [...list, r.data]);
    }
    setFormOpen(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Видалити "${c.name}"?`)) return;
    await rndService.deleteContractor(c.id);
    setContractorList(list => list.filter(x => x.id !== c.id));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Контрагенти R&D
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNew}>Додати</Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {contractorList.length === 0 ? (
            <Typography sx={{ p: 3, color: 'text.secondary' }}>Жодного контрагента. Додайте першого.</Typography>
          ) : (
            contractorList.map((c) => (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600}>{c.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.3 }}>
                    {c.edrpou && <Typography variant="caption" color="text.secondary">ЄДРПОУ: {c.edrpou}</Typography>}
                    {c.contact_full_name && <Typography variant="caption" color="text.secondary">{c.contact_full_name}</Typography>}
                    {c.contact_phone && <Typography variant="caption" color="text.secondary">{c.contact_phone}</Typography>}
                    {c.contact_email && <Typography variant="caption" color="text.secondary">{c.contact_email}</Typography>}
                  </Box>
                  {c.address && <Typography variant="caption" color="text.secondary">{c.address}</Typography>}
                </Box>
                <IconButton size="small" onClick={() => openEdit(c)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                <IconButton size="small" color="error" onClick={() => remove(c)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати контрагента' : 'Новий контрагент'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Назва *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} fullWidth autoFocus />
          <TextField label="ЄДРПОУ" value={form.edrpou} onChange={(e) => setForm(f => ({ ...f, edrpou: e.target.value }))} fullWidth />
          <TextField label="Адреса" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} multiline rows={2} fullWidth />
          <TextField label="Контактна особа" value={form.contact_full_name} onChange={(e) => setForm(f => ({ ...f, contact_full_name: e.target.value }))} fullWidth />
          <TextField label="Телефон" value={form.contact_phone} onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))} fullWidth />
          <TextField label="Email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} fullWidth />
          <TextField label="Нотатки" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} multiline rows={3} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={save} disabled={!form.name}>Зберегти</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Board Form ───────────────────────────────────────────────────────────────
function BoardForm({ open, onClose, onSaved, editing, staffList, contractorList }) {
  const [form, setForm] = useState({ name: '', description: '', color: '#1976d2', members: [], contractors: [] });
  useEffect(() => {
    if (editing) setForm({ name: editing.name, description: editing.description || '', color: editing.color || '#1976d2', members: editing.members || [], contractors: editing.contractors || [] });
    else setForm({ name: '', description: '', color: '#1976d2', members: [], contractors: [] });
  }, [editing, open]);

  const save = async () => {
    if (editing) {
      await rndService.updateBoard(editing.id, form);
    } else {
      const r = await rndService.createBoard(form);
      await rndService.createDefaultStatuses(r.data.id);
    }
    onSaved(); onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Редагувати дошку' : 'Нова R&D дошка'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField label="Назва *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} fullWidth autoFocus />
        <TextField label="Опис" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Колір дошки:</Typography>
          <input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
            style={{ width: 36, height: 30, cursor: 'pointer', border: 'none', borderRadius: 4 }} />
        </Box>
        <Autocomplete multiple options={staffList} getOptionLabel={(o) => o.full_name || o.username}
          value={staffList.filter((s) => form.members.includes(s.id))}
          onChange={(_, val) => setForm(f => ({ ...f, members: val.map(v => v.id) }))}
          renderTags={(val, getProps) => val.map((opt, i) => <Chip key={opt.id} label={opt.full_name || opt.username} size="small" {...getProps({ index: i })} />)}
          renderInput={(params) => <TextField {...params} label="Учасники" />}
        />
        <Autocomplete multiple options={contractorList} getOptionLabel={(o) => o.name}
          value={contractorList.filter((c) => form.contractors.includes(c.id))}
          onChange={(_, val) => setForm(f => ({ ...f, contractors: val.map(v => v.id) }))}
          renderTags={(val, getProps) => val.map((opt, i) => <Chip key={opt.id} label={opt.name} size="small" {...getProps({ index: i })} />)}
          renderInput={(params) => <TextField {...params} label="Контрагенти" />}
          noOptionsText="Нічого не знайдено"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button variant="contained" onClick={save} disabled={!form.name}>Зберегти</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Task Form ────────────────────────────────────────────────────────────────
function TaskForm({ open, onClose, onSaved, boardId, groups, staffList, defaultStatusId, currentUser, statuses }) {
  const [form, setForm] = useState({ title: '', description: '', status: '', priority: 'medium', deadline: '', assignees: [], group: '' });
  useEffect(() => { setForm(f => ({ ...f, status: defaultStatusId || statuses[0]?.id || '' })); }, [defaultStatusId, open, statuses]);

  const save = async () => {
    await rndService.createTask({
      ...form, board: boardId, group: form.group || null,
      created_by: currentUser?.id || null, deadline: form.deadline || null,
      status: form.status || null,
    });
    onSaved(); onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Нова задача</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField label="Назва *" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} fullWidth autoFocus />
        <TextField label="Опис" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Статус</InputLabel>
            <Select value={form.status} label="Статус" onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                    {s.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Пріоритет</InputLabel>
            <Select value={form.priority} label="Пріоритет" onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}>
              {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.icon} {p.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField type="date" label="Дедлайн" InputLabelProps={{ shrink: true }} value={form.deadline}
            onChange={(e) => setForm(f => ({ ...f, deadline: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Група</InputLabel>
            <Select value={form.group} label="Група" onChange={(e) => setForm(f => ({ ...f, group: e.target.value }))}>
              <MenuItem value="">— без групи —</MenuItem>
              {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Autocomplete multiple size="small" options={staffList}
          getOptionLabel={(o) => o.full_name || o.username}
          value={staffList.filter((s) => form.assignees.includes(s.id))}
          onChange={(_, val) => setForm(f => ({ ...f, assignees: val.map(v => v.id) }))}
          renderTags={(val, getProps) => val.map((opt, i) => <Chip key={opt.id} label={opt.full_name || opt.username} size="small" {...getProps({ index: i })} />)}
          renderInput={(params) => <TextField {...params} label="Виконавці" />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button variant="contained" onClick={save} disabled={!form.title}>Створити</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Subtask Drawer ───────────────────────────────────────────────────────────
function SubtaskDrawer({ subtaskId, open, onClose, onSaved, staffList, currentUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [editDesc, setEditDesc] = useState(false);
  const [form, setForm] = useState({});
  const [newComment, setNewComment] = useState('');

  const load = useCallback(async () => {
    if (!subtaskId) return;
    setLoading(true);
    const r = await rndService.getSubtask(subtaskId);
    setData(r.data);
    setForm({ title: r.data.title, description: r.data.description || '', priority: r.data.priority || 'medium', deadline: r.data.deadline || '', assignee: r.data.assignee || '' });
    setLoading(false);
  }, [subtaskId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = async (d) => { await rndService.updateSubtask(subtaskId, d); load(); onSaved(); };

  const addComment = async () => {
    if (!newComment.trim()) return;
    await rndService.createComment({ subtask: subtaskId, text: newComment.trim(), author: currentUser?.id });
    setNewComment(''); load();
  };

  const pi = priorityInfo(data?.priority || 'medium');

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 520 }, display: 'flex', flexDirection: 'column' } }}>
      {loading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Підзадача</Typography>
            <IconButton size="small" onClick={() => { patch({ is_done: !data.is_done }); }}>
              {data.is_done
                ? <CheckBoxIcon fontSize="small" sx={{ color: 'success.main' }} />
                : <CheckBoxOutlineBlankIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
              {editTitle ? (
                <TextField fullWidth variant="standard" value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  onBlur={() => { patch({ title: form.title }); setEditTitle(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { patch({ title: form.title }); setEditTitle(false); } }}
                  autoFocus inputProps={{ style: { fontWeight: 700, fontSize: 18, textDecoration: data.is_done ? 'line-through' : 'none' } }} />
              ) : (
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: 18, cursor: 'text', textDecoration: data.is_done ? 'line-through' : 'none', color: data.is_done ? 'text.secondary' : 'text.primary', '&:hover': { opacity: 0.8 } }}
                  onClick={() => setEditTitle(true)}>
                  {data.title}
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Пріоритет</Typography>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select value={form.priority} onChange={(e) => { setForm(f => ({ ...f, priority: e.target.value })); patch({ priority: e.target.value }); }} variant="outlined" sx={{ fontSize: 12 }}>
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p.value} value={p.value} sx={{ fontSize: 12 }}>
                        <span style={{ color: p.color, marginRight: 6 }}>{p.icon}</span> {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Дедлайн</Typography>
                <TextField type="date" size="small" value={form.deadline}
                  onChange={(e) => { setForm(f => ({ ...f, deadline: e.target.value })); patch({ deadline: e.target.value || null }); }}
                  InputLabelProps={{ shrink: true }} sx={{ width: 160, '& input': { fontSize: 12 } }} />
              </Box>
              {data.completed_at && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Виконано</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckBoxIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                      {new Date(data.completed_at).toLocaleDateString('uk-UA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Виконавець</Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select value={form.assignee || ''} displayEmpty onChange={(e) => { setForm(f => ({ ...f, assignee: e.target.value })); patch({ assignee: e.target.value || null }); }} sx={{ fontSize: 12 }}>
                    <MenuItem value=""><em>— не призначено —</em></MenuItem>
                    {staffList.map((s) => <MenuItem key={s.id} value={s.id} sx={{ fontSize: 12 }}>{s.full_name || s.username}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>Опис</Typography>
              {editDesc ? (
                <TextField fullWidth multiline minRows={3} size="small" value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  onBlur={() => { patch({ description: form.description }); setEditDesc(false); }}
                  autoFocus />
              ) : (
                <Typography variant="body2" onClick={() => setEditDesc(true)} sx={{
                  whiteSpace: 'pre-wrap', fontSize: 13, cursor: 'text', minHeight: 36, p: 1, borderRadius: 1,
                  color: data.description ? 'text.primary' : 'text.disabled',
                  border: '1px dashed transparent', '&:hover': { borderColor: 'divider' },
                }}>
                  {data.description || 'Натисніть щоб додати опис...'}
                </Typography>
              )}
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Коментарі {data.comments?.length > 0 && `(${data.comments.length})`}
              </Typography>
              {(data.comments || []).map((c) => (
                <Box key={c.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'secondary.main' }}>{initials(c.author_name)}</Avatar>
                    <Typography variant="caption" fontWeight={700}>{c.author_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: 10 }}>
                      {new Date(c.created_at).toLocaleDateString('uk-UA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 4, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
                  </Box>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.main', flexShrink: 0 }}>
                  {initials(currentUser?.full_name || currentUser?.username)}
                </Avatar>
                <TextField multiline maxRows={4} size="small" placeholder="Написати коментар..." value={newComment}
                  onChange={(e) => setNewComment(e.target.value)} sx={{ flex: 1, '& textarea': { fontSize: 13 } }} />
                <IconButton onClick={addComment} color="primary" disabled={!newComment.trim()} sx={{ flexShrink: 0 }}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
}

// ─── Group Form ───────────────────────────────────────────────────────────────
function GroupForm({ open, onClose, onSaved, boardId, editing }) {
  const [form, setForm] = useState({ name: '', color: '#757575' });
  useEffect(() => {
    setForm(editing ? { name: editing.name, color: editing.color || '#757575' } : { name: '', color: '#757575' });
  }, [editing, open]);

  const save = async () => {
    if (editing) await rndService.updateGroup(editing.id, form);
    else await rndService.createGroup({ ...form, board: boardId, order: Date.now() });
    onSaved(); onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? 'Редагувати групу' : 'Нова група'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField label="Назва *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} fullWidth autoFocus />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Колір:</Typography>
          <input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
            style={{ width: 36, height: 30, cursor: 'pointer', border: 'none', borderRadius: 4 }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button variant="contained" onClick={save} disabled={!form.name}>Зберегти</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function RnDPage({ currentUser }) {
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boardData, setBoardData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [contractorList, setContractorList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');

  const [boardForm, setBoardForm] = useState({ open: false, editing: null });
  const [taskForm, setTaskForm] = useState({ open: false, defaultStatusId: null });
  const [groupForm, setGroupForm] = useState({ open: false, editing: null });
  const [statusSettings, setStatusSettings] = useState(false);
  const [contractorsDialog, setContractorsDialog] = useState(false);
  const [drawerTaskId, setDrawerTaskId] = useState(null);
  const [drawerSubtaskId, setDrawerSubtaskId] = useState(null);
  const [boardMenuAnchor, setBoardMenuAnchor] = useState(null);
  const [boardMenuTarget, setBoardMenuTarget] = useState(null);

  const loadBoards = useCallback(async () => {
    const [br, sr, cr] = await Promise.all([rndService.getBoards(), api.get('/staff-mini/'), rndService.getContractors({ page_size: 1000 })]);
    const list = br.data.results || br.data;
    setBoards(list);
    setStaffList(sr.data.results || sr.data);
    setContractorList(cr.data.results || cr.data);
    if (list.length > 0 && !selectedBoard) setSelectedBoard(list[0].id);
  }, [selectedBoard]);

  const loadBoard = useCallback(async () => {
    if (!selectedBoard) return;
    setLoading(true);
    const [br, tr] = await Promise.all([
      rndService.getBoard(selectedBoard),
      rndService.getTasks({ board: selectedBoard }),
    ]);
    setBoardData(br.data);
    setTasks(tr.data.results || tr.data);
    setLoading(false);
  }, [selectedBoard]);

  useEffect(() => { loadBoards(); }, []);
  useEffect(() => { loadBoard(); }, [selectedBoard]);

  const statuses = boardData?.statuses || [];
  const groups = boardData?.groups || [];

  const handleDrop = async (e, statusId) => {
    const taskId = parseInt(e.dataTransfer.getData('taskId'));
    if (!taskId) return;
    setTasks((prev) => prev.map((t) => {
      if (t.id !== taskId) return t;
      const sd = statuses.find(s => s.id === statusId);
      return { ...t, status: statusId, status_data: sd || t.status_data };
    }));
    await rndService.moveTask(taskId, { status: statusId });
    loadBoard();
  };

  const handleStatusChange = async (taskId, status) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: status.id, status_data: status } : t));
    await rndService.moveTask(taskId, { status: status.id });
    loadBoard();
  };

  const currentBoard = boards.find((b) => b.id === selectedBoard);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box sx={{ width: 220, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: 13 }}>R&amp;D Проекти</Typography>
          <Tooltip title="Нова дошка">
            <IconButton size="small" onClick={() => setBoardForm({ open: true, editing: null })}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
          {boards.map((b) => (
            <Box key={b.id}
              sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.7, cursor: 'pointer', mx: 0.5, borderRadius: 1,
                bgcolor: selectedBoard === b.id ? 'primary.main' : 'transparent',
                color: selectedBoard === b.id ? 'primary.contrastText' : 'text.primary',
                '&:hover': { bgcolor: selectedBoard === b.id ? 'primary.dark' : 'action.hover' },
              }}
              onClick={() => setSelectedBoard(b.id)}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: selectedBoard === b.id ? 'white' : b.color, mr: 1, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontSize: 13, fontWeight: selectedBoard === b.id ? 700 : 400 }}>
                  {b.name}
                </Typography>
                {b.tasks_total > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                    <LinearProgress variant="determinate"
                      value={Math.round(b.tasks_done / b.tasks_total * 100)}
                      sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: selectedBoard === b.id ? 'rgba(255,255,255,0.3)' : 'action.disabledBackground',
                        '& .MuiLinearProgress-bar': { bgcolor: selectedBoard === b.id ? 'white' : (b.tasks_done === b.tasks_total ? 'success.main' : b.color) } }} />
                    <Typography variant="caption" sx={{ fontSize: 9, opacity: 0.8, flexShrink: 0 }}>
                      {Math.round(b.tasks_done / b.tasks_total * 100)}%
                    </Typography>
                  </Box>
                )}
              </Box>
              <IconButton size="small" sx={{ p: 0.2, color: 'inherit', opacity: 0.5, '&:hover': { opacity: 1 } }}
                onClick={(e) => { e.stopPropagation(); setBoardMenuAnchor(e.currentTarget); setBoardMenuTarget(b); }}>
                <MoreVertIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
          {boards.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block', mt: 2 }}>
              Ще немає дошок
            </Typography>
          )}
        </Box>
      </Box>

      {/* Main */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' }}>
        {/* Toolbar */}
        {currentBoard && (
          <Box sx={{ px: 2.5, py: 1.2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, bgcolor: 'background.paper' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: currentBoard.color }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: 15 }}>{currentBoard.name}</Typography>
            {currentBoard.description && (
              <Typography variant="caption" color="text.secondary">{currentBoard.description}</Typography>
            )}
            {boardData?.contractors_data?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {boardData.contractors_data.map((c) => (
                  <Chip key={c.id} label={c.name} size="small" variant="outlined" />
                ))}
              </Box>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Налаштування статусів">
                <IconButton size="small" onClick={() => setStatusSettings(true)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
                <ToggleButton value="kanban" sx={{ px: 1 }}><ViewKanbanIcon sx={{ fontSize: 16 }} /></ToggleButton>
                <ToggleButton value="list" sx={{ px: 1 }}><ViewListIcon sx={{ fontSize: 16 }} /></ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" variant="outlined"
                onClick={() => setContractorsDialog(true)} sx={{ fontSize: 12 }}>
                Контрагенти
              </Button>
              <Button size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={() => setGroupForm({ open: true, editing: null })} sx={{ fontSize: 12 }}>
                Група
              </Button>
              <Button size="small" variant="contained" startIcon={<AddIcon />}
                onClick={() => setTaskForm({ open: true, defaultStatusId: statuses[0]?.id || null })} sx={{ fontSize: 12 }}>
                Задача
              </Button>
            </Box>
          </Box>
        )}

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : !selectedBoard || !boardData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 2 }}>
            <Typography color="text.secondary">Оберіть дошку або створіть нову</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBoardForm({ open: true, editing: null })}>
              Створити дошку
            </Button>
          </Box>
        ) : statuses.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 2 }}>
            <Typography color="text.secondary">Немає статусів — налаштуйте їх</Typography>
            <Button variant="contained" startIcon={<SettingsIcon />} onClick={() => setStatusSettings(true)}>
              Налаштувати статуси
            </Button>
          </Box>
        ) : view === 'kanban' ? (
          /* KANBAN */
          <Box sx={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, height: '100%', minWidth: 'max-content', alignItems: 'flex-start', pt: 0.5 }}>
              {statuses.map((s) => (
                <KanbanColumn key={s.id} status={s}
                  tasks={tasks.filter(t => t.status === s.id || t.status_data?.id === s.id)}
                  onOpen={setDrawerTaskId} onSubtaskOpen={setDrawerSubtaskId} onDrop={handleDrop}
                  onAddTask={(sid) => setTaskForm({ open: true, defaultStatusId: sid })}
                  statuses={statuses} onStatusChange={handleStatusChange}
                />
              ))}
            </Box>
          </Box>
        ) : (
          /* LIST VIEW */
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Groups */}
            {groups.map((g) => {
              const gt = tasks.filter(t => t.group === g.id);
              return (
                <Box key={g.id} sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: 1, bgcolor: g.color }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{g.name}</Typography>
                    <Chip label={gt.length} size="small" sx={{ height: 18, fontSize: 11 }} />
                    <IconButton size="small" onClick={() => setGroupForm({ open: true, editing: g })}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" color="error" onClick={async () => {
                      if (window.confirm('Видалити групу?')) { await rndService.deleteGroup(g.id); loadBoard(); }
                    }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                  </Box>
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.8, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 14 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Статус</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Назва</Typography>
                      <Typography variant="caption" color="text.secondary">Дедлайн</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>Виконавці</Typography>
                    </Box>
                    {gt.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>Немає задач</Typography>
                    ) : (
                      gt.map(t => <ListRow key={t.id} task={t} onOpen={setDrawerTaskId} onSubtaskOpen={setDrawerSubtaskId} statuses={statuses} onStatusChange={handleStatusChange} />)
                    )}
                    <Box sx={{ px: 2, py: 0.8 }}>
                      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                        onClick={() => setTaskForm({ open: true, defaultStatusId: statuses[0]?.id || null })}
                        sx={{ fontSize: 11, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                        Додати задачу
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              );
            })}

            {/* Ungrouped */}
            {(() => {
              const ug = tasks.filter(t => !t.group);
              if (ug.length === 0 && groups.length > 0) return null;
              return (
                <Box>
                  {groups.length > 0 && <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, px: 0.5 }}>Без групи</Typography>}
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.8, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 14 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ width: 100 }}>Статус</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Назва</Typography>
                      <Typography variant="caption" color="text.secondary">Дедлайн</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>Виконавці</Typography>
                    </Box>
                    {ug.length === 0 ? (
                      <Box sx={{ px: 2, py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography color="text.secondary" variant="body2">Немає задач</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => setTaskForm({ open: true, defaultStatusId: statuses[0]?.id || null })}>
                          Додати першу задачу
                        </Button>
                      </Box>
                    ) : (
                      ug.map(t => <ListRow key={t.id} task={t} onOpen={setDrawerTaskId} onSubtaskOpen={setDrawerSubtaskId} statuses={statuses} onStatusChange={handleStatusChange} />)
                    )}
                    {ug.length > 0 && (
                      <Box sx={{ px: 2, py: 0.8 }}>
                        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                          onClick={() => setTaskForm({ open: true, defaultStatusId: statuses[0]?.id || null })}
                          sx={{ fontSize: 11, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          Додати задачу
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              );
            })()}
          </Box>
        )}
      </Box>

      {/* Dialogs & Drawers */}
      <BoardForm open={boardForm.open} editing={boardForm.editing} staffList={staffList} contractorList={contractorList}
        onClose={() => setBoardForm({ open: false, editing: null })}
        onSaved={() => { loadBoards(); loadBoard(); }} />

      <TaskForm open={taskForm.open} defaultStatusId={taskForm.defaultStatusId}
        boardId={selectedBoard} groups={groups} staffList={staffList}
        currentUser={currentUser} statuses={statuses}
        onClose={() => setTaskForm({ open: false, defaultStatusId: null })}
        onSaved={loadBoard} />

      <GroupForm open={groupForm.open} editing={groupForm.editing} boardId={selectedBoard}
        onClose={() => setGroupForm({ open: false, editing: null })}
        onSaved={loadBoard} />

      <StatusSettings open={statusSettings} onClose={() => setStatusSettings(false)}
        boardId={selectedBoard} statuses={statuses}
        onSaved={loadBoard} />

      <ContractorsDirectoryDialog open={contractorsDialog} onClose={() => { setContractorsDialog(false); loadBoards(); }}
        contractorList={contractorList} setContractorList={setContractorList} />

      {drawerTaskId && (
        <TaskDrawer taskId={drawerTaskId} open={Boolean(drawerTaskId)}
          onClose={() => setDrawerTaskId(null)} onSaved={loadBoard}
          staffList={staffList} currentUser={currentUser} statuses={statuses}
          onSubtaskOpen={setDrawerSubtaskId} />
      )}

      {drawerSubtaskId && (
        <SubtaskDrawer subtaskId={drawerSubtaskId} open={Boolean(drawerSubtaskId)}
          onClose={() => setDrawerSubtaskId(null)} onSaved={loadBoard}
          staffList={staffList} currentUser={currentUser} />
      )}

      {/* Board menu */}
      <Menu anchorEl={boardMenuAnchor} open={Boolean(boardMenuAnchor)} onClose={() => setBoardMenuAnchor(null)}>
        <MenuItem onClick={() => { setBoardMenuAnchor(null); setBoardForm({ open: true, editing: boardMenuTarget }); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Редагувати</ListItemText>
        </MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={async () => {
          setBoardMenuAnchor(null);
          if (window.confirm(`Видалити дошку "${boardMenuTarget?.name}"?`)) {
            await rndService.deleteBoard(boardMenuTarget.id);
            setSelectedBoard(null); setBoardData(null); loadBoards();
          }
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Видалити</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
